import { defineStore } from 'pinia';
import type { Project } from '~/utilities/pyscript-api-models';

interface State {
  show: boolean;
  project: Project | null;
  version: string | null;
  excludeVersion: boolean;
}

export const useShareModalStore = defineStore('share-modal', {
  state: (): State => ({
    show: false,
    project: null,
    version: null,

    // Determines if the version should be excluded from the URLs to be copied
    excludeVersion: false,
  }),

  getters: {
    /**
     * Constructs the URL to be copied. If it's a URL for a specific version,
     * the URL is constructed by utilizing the latest URL and replacing part
     * of the path with the specific version.
     *
     * Example 1: https://username.pyscriptapps.com/project-name/latest/
     * Example 2: https://username.pyscriptapps.com/project-name/v1/
     */
    siteUrlToCopy(state) {
      const latestUrl = state.project?.latest.url;

      if (!latestUrl) {
        return '';
      }

      const { origin } = new URL(latestUrl);
      return state.excludeVersion
        ? `${origin}/${state.project?.slug}/`
        : `${origin}/${state.project?.slug}/${state.version}/`;
    },

    codeUrlToCopy(state) {
      const origin = window.location.origin;
      const usernameOrId = state.project?.username || state.project?.user_id;

      return state.excludeVersion
        ? `${origin}/@${usernameOrId}/${state.project?.slug}/`
        : `${origin}/@${usernameOrId}/${state.project?.slug}/${state.version}`;
    },

    projectId(state) {
      return state.project?.id || '';
    },
  },

  actions: {
    open(project: Project, version = 'latest', excludeVersion = false) {
      this.version = version;
      this.project = project;
      this.show = true;
      this.excludeVersion = excludeVersion;
    },
    close() {
      // Reset the state back to the defaults
      this.$reset();
    },
  },
});
